import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  FilterToolbar,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import {
  AdminAdvisoryOrganizationWhiteLabelRes,
  getGetAdminAdvisoryOrganizationWhiteLabelsQueryKey,
  useDeleteWhitelabel,
  useGetAdminAdvisoryOrganizationWhiteLabels
} from '@ijoin/hooks';
import { DeleteOutline, Edit } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { camelCase } from 'lodash';
import { FC, useMemo, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { WhiteLabelEditDialog } from './WhiteLabelEditDialog';

type WhiteLabelsTabProps = {
  advisoryOrganizationId: string;
};

export const WhiteLabelsTab: FC<WhiteLabelsTabProps> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const whitelabels = useGetAdminAdvisoryOrganizationWhiteLabels(
    props.advisoryOrganizationId,
    { query: { enabled: !!props.advisoryOrganizationId } }
  );

  const hostSuffix = useMemo(
    () => '.' + window.location.host.replace(/^control-panel\./, ''),
    []
  );

  const [isEditOpen, toggleEditOpen] = useToggle();
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | undefined>(
    undefined
  );

  const onInvalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: getGetAdminAdvisoryOrganizationWhiteLabelsQueryKey(
        props.advisoryOrganizationId
      )
    });

  const deleteWhitelabel = useDeleteWhitelabel({
    mutation: {
      onError: (err: any) => {
        const message: string =
          err?.response?.data?.message ??
          err?.message ??
          'Failed to delete white label.';
        notifications.error(message);
      },
      onSuccess: () => {
        onInvalidateList();
        notifications.success('White label deleted.');
      }
    }
  });

  const onAddClick = () => {
    if (!props.advisoryOrganizationId) return;
    setEditingId(undefined);
    toggleEditOpen();
  };

  const onEditRow = (whiteLabelId: string) => {
    setEditingId(whiteLabelId);
    toggleEditOpen();
  };

  const onDeleteRow = (whiteLabelId: string) => {
    setPendingDeleteId(whiteLabelId);
  };

  const onConfirmDelete = () => {
    if (!pendingDeleteId) return;
    deleteWhitelabel.mutate({ whiteLabelId: pendingDeleteId });
    setPendingDeleteId(undefined);
  };

  const onCancelDelete = () => {
    setPendingDeleteId(undefined);
  };

  const onEditClose = () => {
    toggleEditOpen();
    setEditingId(undefined);
  };

  const pendingDeleteRow = useMemo(
    () =>
      (whitelabels.data ?? []).find(
        row => row.whiteLabelId === pendingDeleteId
      ),
    [whitelabels.data, pendingDeleteId]
  );

  const columns: GridColDef<AdminAdvisoryOrganizationWhiteLabelRes>[] = [
    {
      field: 'name',
      flex: 1,
      headerName: 'Name',
      minWidth: 200
    },
    {
      field: 'subdomain',
      flex: 1,
      headerName: 'Preview',
      minWidth: 240,
      renderCell: cell =>
        cell.value ? `http://${cell.value}${hostSuffix}` : ''
    },
    {
      align: 'right',
      field: 'plansInUse',
      headerAlign: 'right',
      headerName: 'Plans',
      width: 100
    },
    {
      align: 'center',
      field: 'isDefault',
      headerAlign: 'center',
      headerName: 'Default',
      renderCell: cell =>
        cell.value ? (
          <StatusBadge severity='success'>Default</StatusBadge>
        ) : null,
      width: 110
    },
    {
      align: 'center',
      field: 'actions',
      headerAlign: 'center',
      headerName: '',
      renderCell: cell => (
        <ActionMenu
          actions={[
            {
              content: 'Delete',
              destructive: true,
              disabled: cell.row.plansInUse > 0 || cell.row.isDefault === true,
              icon: <DeleteOutline />,
              onSelect: () => onDeleteRow(cell.row.whiteLabelId),
              tooltip:
                cell.row.plansInUse > 0
                  ? 'Cannot delete: in use by plans'
                  : cell.row.isDefault
                    ? 'Cannot delete: default white label'
                    : undefined
            },
            {
              content: 'Edit',
              icon: <Edit />,
              onSelect: () => onEditRow(cell.row.whiteLabelId)
            }
          ]}
          trigger={
            <IconButton
              data-testid={`actionsWhitelabel-${camelCase(cell.row.name ?? cell.row.whiteLabelId)}`}>
              <MoreVertIcon />
            </IconButton>
          }
        />
      ),
      sortable: false,
      width: 67
    }
  ];

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        getRowId={row => row.whiteLabelId}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25
            }
          },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
        }}
        loading={whitelabels.isFetching}
        pageSizeOptions={[25, 50, 100]}
        pinnedColumns={{ right: ['actions'] }}
        rows={whitelabels.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'whitelabels'
          },
          toolbar: {
            addDisabled: !props.advisoryOrganizationId,
            addLabel: 'White Label',
            addTestId: 'addWhitelabel',
            onAdd: onAddClick,
            onRefresh: whitelabels.refetch
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sx={{ flex: 1, minHeight: 0 }}
      />
      <WhiteLabelEditDialog
        advisoryOrganizationId={props.advisoryOrganizationId}
        isOpen={isEditOpen}
        onClose={onEditClose}
        whiteLabelId={editingId}
      />
      <ConfirmationDialog
        confirmationText={pendingDeleteRow?.name ?? undefined}
        confirmColor='error'
        confirmText='Delete'
        data-testid='deleteWhitelabelConfirmation'
        isOpen={!!pendingDeleteId}
        message={
          pendingDeleteRow?.name
            ? `Type the white label name to permanently delete "${pendingDeleteRow.name}". This cannot be undone.`
            : 'Are you sure you want to delete this white label? This cannot be undone.'
        }
        onCancel={onCancelDelete}
        onConfirm={onConfirmDelete}
        title='Delete white label'
      />
    </Stack>
  );
};

WhiteLabelsTab.displayName = 'WhiteLabelsTab';
